import { type ReactNode, useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { AssignClientMeta } from "@/components/assign-client-meta";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useStore } from "@/lib/store";
import { useTheme } from "@/components/theme-provider";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowUpDown,
  BarChart3,
  Bookmark,
  Building2,
  CalendarRange,
  Eye,
  Facebook,
  FileText,
  Heart,
  ImageOff,
  Instagram,
  Linkedin,
  Loader2,
  MessageCircle,
  Music2,
  Play,
  Repeat2,
  Reply,
  Share2,
  TrendingUp,
  Users,
  Youtube,
} from "lucide-react";

interface IgAccount {
  igAccountId: string;
  username: string;
  pageId: string;
}

interface FbPage {
  pageId: string;
  pageName: string;
}

interface TikTokAccount {
  connected: boolean;
  username?: string;
}

interface LinkedInAccount {
  connected: boolean;
  name?: string;
}

type MetaConnectionState =
  | "connected"
  | "not_connected"
  | "token_expired"
  | "no_client_mapping"
  | "admin_lost_access";

interface MetaConnectionInfo {
  state: MetaConnectionState;
  message?: string;
  missingFacebookPageIds?: string[];
  missingInstagramActorIds?: string[];
}

interface AccountsResponse {
  instagram: IgAccount[];
  facebook: FbPage[];
  tiktok?: TikTokAccount;
  linkedin?: LinkedInAccount;
  meta?: MetaConnectionInfo;
}

interface ContentInsight {
  id: string;
  platform: "instagram" | "facebook" | "youtube" | "tiktok" | "linkedin";
  contentType: "post" | "story" | "reel" | "fb_post" | "yt_video" | "yt_short" | "tiktok_video";
  caption?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  timestamp: string;
  metrics: Record<string, number>;
}

interface TikTokVideoInsight {
  id: string;
  platform: "tiktok";
  contentType: "tiktok_video";
  caption?: string;
  thumbnailUrl?: string;
  timestamp: string;
  metrics: { likes: number; comments: number; shares: number; views: number };
}

interface YoutubeVideo {
  videoId: string;
  title: string;
  publishedAt: string;
  thumbnailUrl: string | null;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  durationSeconds: number;
  isShort: boolean;
}

type AnalyticsPlatform = "instagram" | "facebook" | "youtube" | "tiktok" | "linkedin";
type AnalyticsScope = "summary" | AnalyticsPlatform;
type DashboardTab = "summary" | "overview" | "posts" | "reels" | "stories";
type SortField = "date" | "impressions" | "comments" | "likes" | "shares";
type SortDir = "asc" | "desc";
type InsightBucket = "post" | "reel" | "story";

const PLATFORM_META: Record<
  AnalyticsPlatform,
  {
    label: string;
    icon: typeof Instagram;
    tone: string;
    toneSoft: string;
    cardGlow: string;
  }
> = {
  instagram: {
    label: "Instagram",
    icon: Instagram,
    tone: "#f472b6",
    toneSoft: "from-fuchsia-500/20 via-pink-500/18 to-orange-400/12",
    cardGlow: "shadow-[0_0_0_1px_rgba(244,114,182,0.16)]",
  },
  facebook: {
    label: "Facebook",
    icon: Facebook,
    tone: "#60a5fa",
    toneSoft: "from-sky-500/18 via-blue-500/16 to-cyan-400/10",
    cardGlow: "shadow-[0_0_0_1px_rgba(96,165,250,0.16)]",
  },
  youtube: {
    label: "YouTube",
    icon: Youtube,
    tone: "#f87171",
    toneSoft: "from-red-500/20 via-rose-500/14 to-orange-400/10",
    cardGlow: "shadow-[0_0_0_1px_rgba(248,113,113,0.16)]",
  },
  tiktok: {
    label: "TikTok",
    icon: Music2,
    tone: "#2dd4bf",
    toneSoft: "from-teal-500/18 via-cyan-500/14 to-sky-400/10",
    cardGlow: "shadow-[0_0_0_1px_rgba(45,212,191,0.16)]",
  },
  linkedin: {
    label: "LinkedIn",
    icon: Linkedin,
    tone: "#60a5fa",
    toneSoft: "from-blue-500/20 via-sky-500/16 to-blue-400/10",
    cardGlow: "shadow-[0_0_0_1px_rgba(96,165,250,0.16)]",
  },
};

const SUMMARY_META = {
  label: "Summary",
  tone: "#c4b5fd",
  toneSoft: "from-violet-500/16 via-fuchsia-500/10 to-sky-500/10",
  cardGlow: "shadow-[0_0_0_1px_rgba(196,181,253,0.14)]",
};

const CONTENT_TYPE_LABELS: Record<
  InsightBucket | "fb_post" | "yt_video" | "yt_short" | "tiktok_video",
  string
> = {
  post: "Post",
  reel: "Reel",
  story: "Story",
  fb_post: "Post FB",
  yt_video: "Video",
  yt_short: "Short",
  tiktok_video: "TikTok",
};

const CONTENT_BUCKET_LABELS: Record<InsightBucket, string> = {
  post: "Posts",
  reel: "Reels",
  story: "Stories",
};

// Light: solid tint + dark text for contrast on white tables.
// dark: variants preserve the original dark-theme look unchanged.
const CONTENT_TYPE_BADGES: Record<
  InsightBucket | "fb_post" | "yt_video" | "yt_short" | "tiktok_video",
  string
> = {
  post: "bg-sky-100 text-sky-700 border border-sky-300 dark:bg-sky-500/14 dark:text-sky-200 dark:border-sky-400/20",
  reel: "bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-300 dark:bg-fuchsia-500/14 dark:text-fuchsia-200 dark:border-fuchsia-400/20",
  story:
    "bg-orange-100 text-orange-700 border border-orange-300 dark:bg-orange-500/14 dark:text-orange-200 dark:border-orange-400/20",
  fb_post:
    "bg-blue-100 text-blue-700 border border-blue-300 dark:bg-blue-500/14 dark:text-blue-200 dark:border-blue-400/20",
  yt_video:
    "bg-red-100 text-red-700 border border-red-300 dark:bg-red-500/14 dark:text-red-200 dark:border-red-400/20",
  yt_short:
    "bg-rose-100 text-rose-700 border border-rose-300 dark:bg-rose-500/14 dark:text-rose-200 dark:border-rose-400/20",
  tiktok_video:
    "bg-teal-100 text-teal-700 border border-teal-300 dark:bg-teal-500/14 dark:text-teal-200 dark:border-teal-400/20",
};

const TAB_LABELS: { id: DashboardTab; label: string }[] = [
  { id: "summary", label: "Summary" },
  { id: "overview", label: "Page overview" },
  { id: "posts", label: "Posts" },
  { id: "reels", label: "Reels" },
  { id: "stories", label: "Stories" },
];

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return Intl.NumberFormat("pl-PL").format(Math.round(n));
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  if (value < 1) return `${value.toFixed(1)}%`;
  return `${Math.round(value)}%`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatShortDayLabel(key: string): string {
  return new Date(`${key}T00:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function getPlatformLabel(platform: AnalyticsPlatform): string {
  return PLATFORM_META[platform].label;
}

function normalizeBucket(contentType: ContentInsight["contentType"]): InsightBucket {
  if (contentType === "reel" || contentType === "yt_short") return "reel";
  if (contentType === "story") return "story";
  return "post";
}

function mapTikTokVideoToInsight(video: TikTokVideoInsight): ContentInsight {
  return {
    id: video.id,
    platform: "tiktok",
    contentType: "tiktok_video",
    caption: video.caption,
    thumbnailUrl: video.thumbnailUrl,
    timestamp: video.timestamp,
    metrics: {
      impressions: video.metrics.views,
      likes: video.metrics.likes,
      comments: video.metrics.comments,
      shares: video.metrics.shares,
    },
  };
}

function mapYouTubeVideoToInsight(video: YoutubeVideo): ContentInsight {
  return {
    id: video.videoId,
    platform: "youtube",
    contentType: video.isShort ? "yt_short" : "yt_video",
    caption: video.title,
    thumbnailUrl: video.thumbnailUrl ?? undefined,
    timestamp: video.publishedAt,
    metrics: {
      impressions: video.viewCount,
      likes: video.likeCount,
      comments: video.commentCount,
    },
  };
}

function getContentTypeLabel(contentType: ContentInsight["contentType"]): string {
  return CONTENT_TYPE_LABELS[contentType] ?? CONTENT_TYPE_LABELS.post;
}

function getContentTypeBadge(contentType: ContentInsight["contentType"]): string {
  return CONTENT_TYPE_BADGES[contentType] ?? CONTENT_TYPE_BADGES.post;
}

function toDayKey(iso: string): string {
  const date = new Date(iso);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getImpressions(metrics: Record<string, number>): number {
  return (
    metrics.impressions ??
    metrics.views ??
    metrics.plays ??
    metrics.ig_reels_aggregated_all_plays_count ??
    metrics.post_impressions ??
    metrics.post_impressions_unique ??
    0
  );
}

function hasImpressionsData(metrics: Record<string, number>): boolean {
  return (
    metrics.impressions !== undefined ||
    metrics.views !== undefined ||
    metrics.plays !== undefined ||
    metrics.ig_reels_aggregated_all_plays_count !== undefined ||
    metrics.post_impressions !== undefined ||
    metrics.post_impressions_unique !== undefined
  );
}

function getReach(metrics: Record<string, number>): number {
  return metrics.reach ?? metrics.post_engaged_users ?? 0;
}

function getLikes(metrics: Record<string, number>): number {
  return metrics.likes ?? metrics.post_reactions_by_type_total ?? 0;
}

function getComments(metrics: Record<string, number>): number {
  return metrics.comments ?? 0;
}

function getShares(metrics: Record<string, number>): number {
  return metrics.shares ?? 0;
}

function getSaved(metrics: Record<string, number>): number {
  return metrics.saved ?? 0;
}

function getReplies(metrics: Record<string, number>): number {
  return metrics.replies ?? 0;
}

function getInteractions(metrics: Record<string, number>): number {
  return (
    getLikes(metrics) +
    getComments(metrics) +
    getShares(metrics) +
    getSaved(metrics) +
    getReplies(metrics)
  );
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getDaySpan(insights: ContentInsight[]): number {
  if (insights.length === 0) return 0;
  const timestamps = insights.map((insight) => new Date(insight.timestamp).getTime());
  const min = Math.min(...timestamps);
  const max = Math.max(...timestamps);
  return Math.max(1, Math.round((max - min) / 86_400_000) + 1);
}

function computeKpis(insights: ContentInsight[]) {
  let totalImpressions = 0;
  let totalReach = 0;
  let totalInteractions = 0;
  let totalLikes = 0;
  let totalComments = 0;
  let totalShares = 0;
  let totalSaved = 0;
  let totalReplies = 0;

  for (const item of insights) {
    totalImpressions += getImpressions(item.metrics);
    totalReach += getReach(item.metrics);
    totalInteractions += getInteractions(item.metrics);
    totalLikes += getLikes(item.metrics);
    totalComments += getComments(item.metrics);
    totalShares += getShares(item.metrics);
    totalSaved += getSaved(item.metrics);
    totalReplies += getReplies(item.metrics);
  }

  const engagementRate =
    totalReach > 0
      ? (totalInteractions / totalReach) * 100
      : totalImpressions > 0
        ? (totalInteractions / totalImpressions) * 100
        : 0;

  const daySpan = getDaySpan(insights);
  const postsPerWeek = daySpan > 0 ? (insights.length / daySpan) * 7 : 0;

  return {
    postCount: insights.length,
    totalImpressions,
    totalReach,
    totalInteractions,
    totalLikes,
    totalComments,
    totalShares,
    totalSaved,
    totalReplies,
    engagementRate,
    avgImpressionsPerContent: insights.length ? totalImpressions / insights.length : 0,
    avgInteractionsPerContent: insights.length ? totalInteractions / insights.length : 0,
    avgReachPerContent: insights.length ? totalReach / insights.length : 0,
    postsPerWeek,
    daySpan,
  };
}

function buildDailySeries(insights: ContentInsight[]) {
  if (insights.length === 0) return [];

  const bucket = new Map<
    string,
    {
      dateKey: string;
      impressions: number;
      reach: number;
      interactions: number;
      contentCount: number;
    }
  >();

  let minDate = new Date(insights[0].timestamp);
  let maxDate = new Date(insights[0].timestamp);

  for (const insight of insights) {
    const currentDate = new Date(insight.timestamp);
    if (currentDate < minDate) minDate = currentDate;
    if (currentDate > maxDate) maxDate = currentDate;

    const dayKey = toDayKey(insight.timestamp);
    const row = bucket.get(dayKey) ?? {
      dateKey: dayKey,
      impressions: 0,
      reach: 0,
      interactions: 0,
      contentCount: 0,
    };

    row.impressions += getImpressions(insight.metrics);
    row.reach += getReach(insight.metrics);
    row.interactions += getInteractions(insight.metrics);
    row.contentCount += 1;
    bucket.set(dayKey, row);
  }

  const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
  const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
  const series: Array<{
    dateKey: string;
    label: string;
    impressions: number;
    reach: number;
    interactions: number;
    contentCount: number;
  }> = [];

  while (cursor <= end) {
    const dateKey = toDayKey(cursor.toISOString());
    const row = bucket.get(dateKey);
    series.push({
      dateKey,
      label: formatShortDayLabel(dateKey),
      impressions: row?.impressions ?? 0,
      reach: row?.reach ?? 0,
      interactions: row?.interactions ?? 0,
      contentCount: row?.contentCount ?? 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return series;
}

function buildWeekdaySeries(insights: ContentInsight[]) {
  const rows = WEEKDAY_LABELS.map((label, index) => ({
    label,
    index,
    contentCount: 0,
    interactions: 0,
    impressions: 0,
    avgInteractions: 0,
  }));

  for (const insight of insights) {
    const dayIndex = (new Date(insight.timestamp).getDay() + 6) % 7;
    rows[dayIndex].contentCount += 1;
    rows[dayIndex].interactions += getInteractions(insight.metrics);
    rows[dayIndex].impressions += getImpressions(insight.metrics);
  }

  for (const row of rows) {
    row.avgInteractions = row.contentCount > 0 ? row.interactions / row.contentCount : 0;
  }

  return rows;
}

function buildContentMix(insights: ContentInsight[]) {
  const rows: Record<
    InsightBucket,
    {
      bucket: InsightBucket;
      label: string;
      count: number;
      impressions: number;
      reach: number;
      interactions: number;
      avgInteractions: number;
    }
  > = {
    post: {
      bucket: "post",
      label: "Posts",
      count: 0,
      impressions: 0,
      reach: 0,
      interactions: 0,
      avgInteractions: 0,
    },
    reel: {
      bucket: "reel",
      label: "Reels",
      count: 0,
      impressions: 0,
      reach: 0,
      interactions: 0,
      avgInteractions: 0,
    },
    story: {
      bucket: "story",
      label: "Stories",
      count: 0,
      impressions: 0,
      reach: 0,
      interactions: 0,
      avgInteractions: 0,
    },
  };

  for (const insight of insights) {
    const bucket = normalizeBucket(insight.contentType);
    rows[bucket].count += 1;
    rows[bucket].impressions += getImpressions(insight.metrics);
    rows[bucket].reach += getReach(insight.metrics);
    rows[bucket].interactions += getInteractions(insight.metrics);
  }

  return Object.values(rows)
    .map((row) => ({
      ...row,
      avgInteractions: row.count > 0 ? row.interactions / row.count : 0,
    }))
    .filter((row) => row.count > 0);
}

function buildCrossPlatformSeries(
  insightsByPlatform: Record<AnalyticsPlatform, ContentInsight[]>,
  metric: "impressions" | "reach" | "interactions" | "contentCount"
) {
  const allInsights = [
    ...insightsByPlatform.instagram,
    ...insightsByPlatform.facebook,
    ...insightsByPlatform.youtube,
    ...insightsByPlatform.tiktok,
  ];
  if (allInsights.length === 0) return [];

  let minDate = new Date(allInsights[0].timestamp);
  let maxDate = new Date(allInsights[0].timestamp);

  for (const insight of allInsights) {
    const date = new Date(insight.timestamp);
    if (date < minDate) minDate = date;
    if (date > maxDate) maxDate = date;
  }

  const bucket = new Map<
    string,
    {
      dateKey: string;
      label: string;
      instagram: number;
      facebook: number;
      youtube: number;
      tiktok: number;
      total: number;
    }
  >();

  const resolveValue = (insight: ContentInsight) => {
    if (metric === "contentCount") return 1;
    if (metric === "impressions") return getImpressions(insight.metrics);
    if (metric === "reach") return getReach(insight.metrics);
    return getInteractions(insight.metrics);
  };

  for (const platform of ["instagram", "facebook", "youtube", "tiktok"] as const) {
    for (const insight of insightsByPlatform[platform]) {
      const dateKey = toDayKey(insight.timestamp);
      const row = bucket.get(dateKey) ?? {
        dateKey,
        label: formatShortDayLabel(dateKey),
        instagram: 0,
        facebook: 0,
        youtube: 0,
        tiktok: 0,
        total: 0,
      };

      const value = resolveValue(insight);
      row[platform] += value;
      row.total += value;
      bucket.set(dateKey, row);
    }
  }

  const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
  const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
  const series: Array<{
    dateKey: string;
    label: string;
    instagram: number;
    facebook: number;
    youtube: number;
    tiktok: number;
    total: number;
  }> = [];

  while (cursor <= end) {
    const dateKey = toDayKey(cursor.toISOString());
    const row = bucket.get(dateKey);
    series.push(
      row ?? {
        dateKey,
        label: formatShortDayLabel(dateKey),
        instagram: 0,
        facebook: 0,
        youtube: 0,
        tiktok: 0,
        total: 0,
      }
    );
    cursor.setDate(cursor.getDate() + 1);
  }

  return series;
}

function formatPeriodLabel(insights: ContentInsight[]) {
  if (insights.length === 0) return "No period";
  const timestamps = insights.map((insight) => new Date(insight.timestamp).getTime());
  const min = new Date(Math.min(...timestamps));
  const max = new Date(Math.max(...timestamps));

  const sameYear = min.getFullYear() === max.getFullYear();
  const start = min.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
  });
  const end = max.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${start} - ${end}`;
}

function getTopContent(insights: ContentInsight[], limit = 6) {
  return [...insights]
    .sort((a, b) => getInteractions(b.metrics) - getInteractions(a.metrics))
    .slice(0, limit);
}

function getScopedInsights(tab: DashboardTab, insights: ContentInsight[]) {
  if (tab === "reels")
    return insights.filter((item) => normalizeBucket(item.contentType) === "reel");
  if (tab === "stories")
    return insights.filter((item) => normalizeBucket(item.contentType) === "story");
  if (tab === "posts")
    return insights.filter((item) => normalizeBucket(item.contentType) === "post");
  return insights;
}

function Surface({
  title,
  subtitle,
  children,
  headerRight,
  className = "",
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  headerRight?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={`border-white/[0.08] bg-card shadow-none ${className}`}>
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-4 border-b border-white/[0.06] px-5 py-4">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            {subtitle ? <p className="text-xs text-white/45">{subtitle}</p> : null}
          </div>
          {headerRight}
        </div>
        <div className="p-5">{children}</div>
      </CardContent>
    </Card>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon,
  accentClass,
}: {
  label: string;
  value: string;
  hint: string;
  icon: ReactNode;
  accentClass: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-muted px-4 py-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] text-white/35">{label}</div>
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${accentClass}`}>
          {icon}
        </div>
      </div>
      <div className="text-2xl font-semibold text-white">{value}</div>
      <div className="mt-1 text-xs text-white/42">{hint}</div>
    </div>
  );
}

function LoadingState() {
  const { t } = useTranslation("contentAnalytics");
  return (
    <div className="flex min-h-[360px] flex-col items-center justify-center text-white/40">
      <Loader2 size={34} className="mb-3 animate-spin" />
      <p className="text-sm">{t("loading")}</p>
    </div>
  );
}

function EmptyState({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] text-center text-white/40">
      <BarChart3 size={38} className="mb-3" />
      <p className="max-w-md text-sm leading-6">{message}</p>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="mt-4 px-4 py-2 bg-[#ef3e29] hover:bg-[#d63520] text-white text-xs font-medium rounded-lg transition-colors"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

interface BannerCta {
  label: string;
  onClick: () => void;
}

function MetaConnectionBanner({
  info,
  clientName,
  clientId,
  canManageIntegrations,
  onGoToIntegrations,
  onGoToClient,
}: {
  info: MetaConnectionInfo;
  clientName?: string;
  clientId?: string;
  canManageIntegrations: boolean;
  onGoToIntegrations: () => void;
  onGoToClient: (clientId?: string) => void;
}) {
  if (info.state === "connected") return null;

  type Cfg = { tone: "amber" | "sky" | "rose"; title: string; body: ReactNode; cta?: BannerCta };

  const cfg: Cfg = (() => {
    switch (info.state) {
      case "not_connected":
        return canManageIntegrations
          ? {
              tone: "amber",
              title: "Meta nie jest podłączona",
              body: clientName
                ? `Aby zobaczyć analitykę klienta „${clientName}", podłącz konto Meta agencji w Centrum Integracji. Po połączeniu wróć tu i przypisz strony klientowi w panelu agencji.`
                : "Podłącz konto Meta, żeby zobaczyć analitykę FB/IG.",
              cta: { label: "Przejdź do integracji", onClick: onGoToIntegrations },
            }
          : {
              tone: "amber",
              title: "Brak połączenia z Meta",
              body: "Twoja agencja nie podłączyła jeszcze konta Meta. Skontaktuj się z administratorem agencji — dopóki nie podłączy Meta, analityka FB/IG nie będzie dostępna.",
            };
      case "token_expired":
        return canManageIntegrations
          ? {
              tone: "amber",
              title: "Token Meta wygasł",
              body: "Token dostępu Meta stracił ważność. Połącz konto ponownie, żeby przywrócić dane.",
              cta: { label: "Połącz Meta ponownie", onClick: onGoToIntegrations },
            }
          : {
              tone: "amber",
              title: "Token Meta wygasł",
              body: "Token Meta agencji stracił ważność. Skontaktuj się z administratorem agencji — musi ponownie połączyć Meta.",
            };
      case "no_client_mapping":
        return canManageIntegrations
          ? {
              tone: "sky",
              title: clientName
                ? `Klient „${clientName}" nie ma przypisanych stron`
                : "Brak przypisanych stron",
              body: "Konto Meta jest podłączone, ale do tego klienta nie przypisano żadnych stron FB / kont IG. Otwórz kartę klienta w panelu agencji i przypisz konkretne strony — analityka pojawi się od razu.",
              cta: { label: "Otwórz kartę klienta", onClick: () => onGoToClient(clientId) },
            }
          : {
              tone: "sky",
              title: "Brak przypisanych stron",
              body: "Twoje konto nie ma jeszcze przypisanych stron FB/IG do analityki. Skontaktuj się z administratorem agencji, żeby dopisał strony do Twojego profilu.",
            };
      case "admin_lost_access": {
        const missingFb = info.missingFacebookPageIds ?? [];
        const missingIg = info.missingInstagramActorIds ?? [];
        const idsBlock = (
          <>
            {missingFb.length > 0 && (
              <p className="mt-2 text-white/60">
                Strony FB bez dostępu:{" "}
                <span className="font-mono text-white/80">{missingFb.join(", ")}</span>
              </p>
            )}
            {missingIg.length > 0 && (
              <p className="mt-1 text-white/60">
                Konta IG bez dostępu:{" "}
                <span className="font-mono text-white/80">{missingIg.join(", ")}</span>
              </p>
            )}
          </>
        );
        return canManageIntegrations
          ? {
              tone: "rose",
              title: "Brak dostępu do przypisanych stron",
              body: (
                <>
                  <p>
                    {clientName
                      ? `Klient „${clientName}" ma przypisane strony, `
                      : "Strony są przypisane, "}
                    ale Twoje konto Meta nie ma do nich aktualnie dostępu. Najczęstsza przyczyna:
                    zmiana ról w Business Manager albo brak uprawnień{" "}
                    <span className="font-mono text-white/70">pages_show_list</span> /{" "}
                    <span className="font-mono text-white/70">business_management</span> przy
                    ostatnim łączeniu.
                  </p>
                  {idsBlock}
                </>
              ),
              cta: { label: "Połącz Meta ponownie", onClick: onGoToIntegrations },
            }
          : {
              tone: "rose",
              title: "Brak dostępu do przypisanych stron",
              body: (
                <>
                  <p>
                    Konto Meta agencji nie ma teraz dostępu do stron przypisanych do Ciebie.
                    Skontaktuj się z administratorem agencji — musi odświeżyć połączenie z Meta.
                  </p>
                  {idsBlock}
                </>
              ),
            };
      }
    }
  })();

  const toneClass: Record<Cfg["tone"], string> = {
    amber: "border-amber-400/30 bg-amber-500/[0.07]",
    sky: "border-sky-400/30 bg-sky-500/[0.07]",
    rose: "border-rose-400/30 bg-rose-500/[0.07]",
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${toneClass[cfg.tone]}`}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1 text-sm leading-6">
          <div className="font-medium text-white">{cfg.title}</div>
          <div className="text-white/70">{cfg.body}</div>
        </div>
        {cfg.cta && (
          <button
            onClick={cfg.cta.onClick}
            className="shrink-0 rounded-md bg-white/[0.08] px-3 py-2 text-xs font-medium text-white hover:bg-white/[0.14] transition-colors"
          >
            {cfg.cta.label}
          </button>
        )}
      </div>
      {info.state === "no_client_mapping" && canManageIntegrations && clientId && (
        <div className="mt-3">
          <AssignClientMeta
            clientId={clientId}
            clientName={clientName}
            invalidateKeys={[["/api/content-analytics/accounts"]]}
          />
        </div>
      )}
    </div>
  );
}

function AnalyticsChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-white/[0.08] bg-popover px-3 py-2 shadow-2xl">
      <div className="mb-2 text-[11px] font-medium text-white/80">{label}</div>
      <div className="space-y-1.5">
        {payload.map((entry: any) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-2 text-white/55">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
              {entry.name}
            </div>
            <span className="font-medium text-white">{formatNumber(entry.value ?? 0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SortableHeader({
  label,
  field,
  currentSort,
  currentDir,
  onSort,
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
}) {
  const active = currentSort === field;

  return (
    <button
      onClick={() => onSort(field)}
      className={`flex items-center justify-center gap-1 text-[10px] font-medium uppercase tracking-[0.16em] transition-colors ${
        active ? "text-white" : "text-white/30 hover:text-white/55"
      }`}
    >
      {label}
      <ArrowUpDown size={10} className={active && currentDir === "asc" ? "rotate-180" : ""} />
    </button>
  );
}

function PostRow({
  insight,
  showPlatform = false,
  onCrosspost,
  crossposting = false,
}: {
  insight: ContentInsight;
  showPlatform?: boolean;
  onCrosspost?: (insight: ContentInsight) => void;
  crossposting?: boolean;
}) {
  const { t } = useTranslation("contentAnalytics");
  const impressions = getImpressions(insight.metrics);
  const comments = getComments(insight.metrics);
  const likes = getLikes(insight.metrics);
  const shares = getShares(insight.metrics);
  const thumbnail = insight.thumbnailUrl ?? insight.mediaUrl;
  const gridClass = showPlatform
    ? "grid grid-cols-[44px_1fr_76px_76px_60px_80px_80px_80px_80px] items-center gap-2 border-b border-white/[0.05] px-4 py-3 transition-colors hover:bg-white/[0.02]"
    : "grid grid-cols-[44px_1fr_76px_60px_80px_80px_80px_80px] items-center gap-2 border-b border-white/[0.05] px-4 py-3 transition-colors hover:bg-white/[0.02]";

  return (
    <div className={gridClass}>
      <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-white/[0.04]">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            className="h-full w-full object-cover"
            onError={(event) => {
              (event.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <ImageOff size={14} className="text-white/20" />
        )}
      </div>

      <div className="min-w-0">
        <p className="line-clamp-3 text-xs leading-5 text-white/80">
          {insight.caption || t("noCaption")}
        </p>
        {onCrosspost ? (
          <button
            type="button"
            onClick={() => onCrosspost(insight)}
            disabled={crossposting}
            className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.04] px-2 py-1 text-[10px] font-medium text-white/60 transition-colors hover:bg-white/[0.08] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {crossposting ? <Loader2 size={11} className="animate-spin" /> : <Repeat2 size={11} />}
            Crosspostuj
          </button>
        ) : null}
      </div>

      <div className="text-right">
        <span
          className={`inline-flex rounded-full px-2 py-1 text-[10px] font-medium ${CONTENT_TYPE_BADGES[insight.contentType]}`}
        >
          {CONTENT_TYPE_LABELS[insight.contentType]}
        </span>
      </div>

      {showPlatform ? (
        <div className="text-right">
          <span className="inline-flex rounded-full border border-white/[0.08] bg-white/[0.04] px-2 py-1 text-[10px] font-medium text-white/72">
            {getPlatformLabel(insight.platform)}
          </span>
        </div>
      ) : null}

      <span className="text-center text-xs text-white/42">{formatDate(insight.timestamp)}</span>
      <span className="text-center text-xs font-medium text-white">
        {hasImpressionsData(insight.metrics) ? formatNumber(impressions) : "—"}
      </span>
      <span className="text-center text-xs font-medium text-white">{formatNumber(comments)}</span>
      <span className="text-center text-xs font-medium text-white">{formatNumber(likes)}</span>
      <span className="text-center text-xs font-medium text-white">{formatNumber(shares)}</span>
    </div>
  );
}

export default function ContentAnalytics({
  onCrosspostRequested,
}: {
  onCrosspostRequested?: () => void;
} = {}) {
  const { t } = useTranslation("contentAnalytics");
  const { toast } = useToast();
  const setPendingCrosspost = useStore((s) => s.setPendingCrosspost);
  const [crosspostingId, setCrosspostingId] = useState<string | null>(null);
  // Recharts applies stroke/fill as SVG presentation attributes (CSS var() won't
  // resolve there), so pick concrete colors from the resolved theme.
  const { resolvedTheme } = useTheme();
  const chartGrid = resolvedTheme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)";
  const chartAxis = resolvedTheme === "dark" ? "rgba(255,255,255,0.42)" : "rgba(0,0,0,0.5)";
  const [, setLocation] = useLocation();
  const goToIntegrations = useCallback(() => setLocation("/integracje?open=meta"), [setLocation]);
  const { user } = useAuth();
  const isSuperAdmin = (user as { role?: string } | undefined)?.role === "super_admin";
  const canManageIntegrations = useMemo(() => {
    const role = (user as { role?: string } | undefined)?.role;
    if (role === "agency_owner" || role === "super_admin") return true;
    const perms = (user as { agencyPermissions?: string[] } | undefined)?.agencyPermissions ?? [];
    return perms.includes("integrations.manage") || perms.includes("assign_ad_accounts");
  }, [user]);
  const [platform, setPlatform] = useState<AnalyticsScope>("summary");
  const [selectedIgAccount, setSelectedIgAccount] = useState("");
  const [selectedFbPage, setSelectedFbPage] = useState("");
  const [tab, setTab] = useState<DashboardTab>("summary");
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const {
    clients: agencyClients,
    isAgencyUser,
    selectedClientUserId,
    setSelectedClient,
  } = useAgencyClients();
  type AgencyClientOption = (typeof agencyClients)[number];

  const forUserId = selectedClientUserId ?? undefined;
  // VP-99: when the client changes via the global selector, reset the IG/FB account selection
  // (previously done inline in the removed Workspace picker's onChange).
  useEffect(() => {
    setSelectedIgAccount("");
    setSelectedFbPage("");
  }, [selectedClientUserId]);
  const forUserParam = forUserId ? `&forUserId=${forUserId}` : "";
  const forUserQuery = forUserId ? `?forUserId=${forUserId}` : "";

  // Crosspost: import this reel's media into our S3 (best-effort — may fail for expired
  // native links), stash it in the store, then ask the parent to switch to the Scheduler.
  const handleCrosspost = useCallback(
    async (insight: ContentInsight) => {
      if (!insight.mediaUrl) {
        toast({ title: "Brak pliku wideo do crosspostu", variant: "destructive" });
        return;
      }
      setCrosspostingId(insight.id);
      try {
        const result = (await apiRequest("POST", "/api/scheduler/import-media", {
          url: insight.mediaUrl,
          ...(forUserId ? { forUserId } : {}),
        })) as { mediaUrl: string; mediaType: "video" | "image" };
        setPendingCrosspost({
          mediaUrl: result.mediaUrl,
          mediaType: result.mediaType,
          caption: insight.caption ?? "",
          sourcePlatform: insight.platform,
        });
        onCrosspostRequested?.();
      } catch {
        toast({
          title: "Nie udało się pobrać wideo",
          description: "Plik mógł wygasnąć. Spróbuj z innego źródła.",
          variant: "destructive",
        });
      } finally {
        setCrosspostingId(null);
      }
    },
    [forUserId, onCrosspostRequested, setPendingCrosspost, toast]
  );

  const { data: accounts, isLoading: accountsLoading } = useQuery<AccountsResponse>({
    queryKey: ["/api/content-analytics/accounts", forUserId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/content-analytics/accounts${forUserQuery}`
      ) as Promise<AccountsResponse>,
  });

  const igAccounts = accounts?.instagram ?? [];
  const fbPages = accounts?.facebook ?? [];
  const tiktokAccount = accounts?.tiktok;
  const linkedinAccount = accounts?.linkedin;
  const metaInfo = accounts?.meta;

  const { data: ytStatus } = useQuery<{
    connected: boolean;
    channelId?: string;
    channelTitle?: string;
  }>({
    queryKey: ["/api/integrations/youtube/status", forUserId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/integrations/youtube/status${forUserId ? `?forUserId=${forUserId}` : ""}`
      ) as Promise<{ connected: boolean; channelId?: string; channelTitle?: string }>,
  });

  const { data: tiktokStatus } = useQuery<{ connected: boolean; tiktokUsername?: string }>({
    queryKey: ["/api/integrations/tiktok/status", forUserId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/integrations/tiktok/status${forUserId ? `?forUserId=${forUserId}` : ""}`
      ) as Promise<{ connected: boolean; tiktokUsername?: string }>,
  });

  const ytConnected = ytStatus?.connected ?? false;
  const tiktokConnected = Boolean(tiktokAccount?.connected || tiktokStatus?.connected);

  const { data: ytVideosData, isFetching: ytFetching } = useQuery<{
    videos: YoutubeVideo[];
    preview: null | unknown;
  }>({
    queryKey: ["/api/integrations/youtube/videos", forUserId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/integrations/youtube/videos${forUserId ? `?forUserId=${forUserId}` : ""}`
      ) as Promise<{ videos: YoutubeVideo[]; preview: null | unknown }>,
    enabled: ytConnected,
  });

  const connectedPlatforms = useMemo(() => {
    const platforms: AnalyticsPlatform[] = [];
    if (igAccounts.length > 0) platforms.push("instagram");
    if (fbPages.length > 0) platforms.push("facebook");
    if (ytConnected) platforms.push("youtube");
    if (tiktokConnected) platforms.push("tiktok");
    if (linkedinAccount?.connected) platforms.push("linkedin");
    return platforms;
  }, [igAccounts.length, fbPages.length, ytConnected, tiktokConnected, linkedinAccount?.connected]);

  useEffect(() => {
    if (
      platform !== "summary" &&
      connectedPlatforms.length > 0 &&
      !connectedPlatforms.includes(platform as AnalyticsPlatform)
    ) {
      setPlatform(connectedPlatforms[0]);
    }
  }, [connectedPlatforms, platform]);

  useEffect(() => {
    if (
      (platform === "facebook" || platform === "youtube" || platform === "tiktok") &&
      tab === "stories"
    ) {
      setTab("posts");
    }
    if (platform === "tiktok" && (tab === "stories" || tab === "reels")) {
      setTab("posts");
    }
    if (platform === "linkedin" && tab !== "summary") {
      setTab("summary");
    }
  }, [platform, tab]);

  const activeIgAccount = selectedIgAccount || igAccounts[0]?.igAccountId || "";
  const activeIgPageId =
    igAccounts.find((account: IgAccount) => account.igAccountId === activeIgAccount)?.pageId ?? "";
  const activeFbPage = selectedFbPage || fbPages[0]?.pageId || "";

  const igEnabled =
    (platform === "instagram" || platform === "summary") && !!activeIgAccount && !!activeIgPageId;
  const fbEnabled = (platform === "facebook" || platform === "summary") && !!activeFbPage;
  const tiktokEnabled = (platform === "tiktok" || platform === "summary") && tiktokConnected;

  const { data: igData, isFetching: igFetching } = useQuery<{
    media: ContentInsight[];
    stories: ContentInsight[];
  }>({
    queryKey: ["/api/content-analytics/instagram", activeIgAccount, activeIgPageId, forUserId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/content-analytics/instagram?accountId=${activeIgAccount}&pageId=${activeIgPageId}${forUserParam}`
      ) as Promise<{ media: ContentInsight[]; stories: ContentInsight[] }>,
    enabled: igEnabled,
  });

  const { data: fbData, isFetching: fbFetching } = useQuery<{ posts: ContentInsight[] }>({
    queryKey: ["/api/content-analytics/facebook", activeFbPage, forUserId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/content-analytics/facebook?pageId=${activeFbPage}${forUserParam}`
      ) as Promise<{ posts: ContentInsight[] }>,
    enabled: fbEnabled,
  });

  const ytInsights = useMemo<ContentInsight[]>(() => {
    if (!ytVideosData?.videos) return [];
    return ytVideosData.videos.map(mapYouTubeVideoToInsight);
  }, [ytVideosData]);

  const { data: tiktokData, isFetching: tiktokFetching } = useQuery<{
    userStats: Record<string, unknown>;
    videos: TikTokVideoInsight[];
  }>({
    queryKey: ["/api/content-analytics/tiktok", forUserId],
    queryFn: () =>
      apiRequest("GET", `/api/content-analytics/tiktok${forUserQuery}`) as Promise<{
        userStats: Record<string, unknown>;
        videos: TikTokVideoInsight[];
      }>,
    enabled: tiktokEnabled,
  });

  const tiktokInsights = useMemo<ContentInsight[]>(() => {
    if (!tiktokData?.videos) return [];
    return tiktokData.videos.map(mapTikTokVideoToInsight);
  }, [tiktokData]);

  const insightsByPlatform = useMemo<Record<AnalyticsPlatform, ContentInsight[]>>(
    () => ({
      instagram: [...(igData?.media ?? []), ...(igData?.stories ?? [])],
      facebook: fbData?.posts ?? [],
      youtube: ytInsights,
      tiktok: tiktokInsights,
      linkedin: [],
    }),
    [igData, fbData, ytInsights, tiktokInsights]
  );

  const allInsights = useMemo(() => {
    if (platform === "summary") {
      return [
        ...insightsByPlatform.instagram,
        ...insightsByPlatform.facebook,
        ...insightsByPlatform.youtube,
        ...insightsByPlatform.tiktok,
        ...insightsByPlatform.linkedin,
      ];
    }
    return insightsByPlatform[platform];
  }, [platform, insightsByPlatform]);

  const scopedInsights = useMemo(() => getScopedInsights(tab, allInsights), [tab, allInsights]);
  const sortedInsights = useMemo(() => {
    const items = [...scopedInsights];
    const getSortValue = (insight: ContentInsight): number => {
      if (sortField === "date") return new Date(insight.timestamp).getTime();
      if (sortField === "impressions") return getImpressions(insight.metrics);
      if (sortField === "comments") return getComments(insight.metrics);
      if (sortField === "likes") return getLikes(insight.metrics);
      return getShares(insight.metrics);
    };

    items.sort((a, b) => {
      const diff = getSortValue(a) - getSortValue(b);
      return sortDir === "desc" ? -diff : diff;
    });
    return items;
  }, [scopedInsights, sortDir, sortField]);

  const allKpis = useMemo(() => computeKpis(allInsights), [allInsights]);
  const scopedKpis = useMemo(() => computeKpis(scopedInsights), [scopedInsights]);
  const dailySeries = useMemo(() => buildDailySeries(scopedInsights), [scopedInsights]);
  const weekdaySeries = useMemo(() => buildWeekdaySeries(scopedInsights), [scopedInsights]);
  const contentMix = useMemo(() => buildContentMix(scopedInsights), [scopedInsights]);
  const topContent = useMemo(() => getTopContent(scopedInsights), [scopedInsights]);
  const overviewSeries = useMemo(() => buildDailySeries(allInsights), [allInsights]);
  const summaryInteractionsSeries = useMemo(
    () => buildCrossPlatformSeries(insightsByPlatform, "interactions"),
    [insightsByPlatform]
  );
  const summaryContentSeries = useMemo(
    () => buildCrossPlatformSeries(insightsByPlatform, "contentCount"),
    [insightsByPlatform]
  );
  const summaryReachSeries = useMemo(
    () => buildCrossPlatformSeries(insightsByPlatform, "reach"),
    [insightsByPlatform]
  );
  const platformSnapshots = useMemo(
    () =>
      connectedPlatforms.map((connectedPlatform) => ({
        platform: connectedPlatform,
        kpis: computeKpis(insightsByPlatform[connectedPlatform]),
        insights: insightsByPlatform[connectedPlatform],
      })),
    [connectedPlatforms, insightsByPlatform]
  );

  const bestWeekday = useMemo(() => {
    return [...weekdaySeries].sort((a, b) => b.avgInteractions - a.avgInteractions)[0];
  }, [weekdaySeries]);

  const activePlatformMeta = platform === "summary" ? SUMMARY_META : PLATFORM_META[platform];
  const ActivePlatformIcon = platform === "summary" ? BarChart3 : PLATFORM_META[platform].icon;
  const isLoading =
    accountsLoading ||
    (igEnabled && igFetching) ||
    (fbEnabled && fbFetching) ||
    (ytConnected && ytFetching) ||
    (tiktokEnabled && tiktokFetching);
  const hasAccounts =
    platform === "summary"
      ? connectedPlatforms.length > 0
      : platform === "instagram"
        ? igAccounts.length > 0
        : platform === "facebook"
          ? fbPages.length > 0
          : platform === "youtube"
            ? ytConnected
            : platform === "tiktok"
              ? tiktokConnected
              : platform === "linkedin"
                ? !!linkedinAccount?.connected
                : false;
  const currentPeriodLabel = formatPeriodLabel(scopedInsights);
  const activeAccountLabel =
    platform === "summary"
      ? `${connectedPlatforms.length} connected platform${connectedPlatforms.length === 1 ? "" : "s"}`
      : platform === "instagram"
        ? igAccounts.find((account: IgAccount) => account.igAccountId === activeIgAccount)?.username
          ? `@${igAccounts.find((account: IgAccount) => account.igAccountId === activeIgAccount)?.username}`
          : "Instagram account"
        : platform === "facebook"
          ? (fbPages.find((page: FbPage) => page.pageId === activeFbPage)?.pageName ??
            "Facebook page")
          : platform === "youtube"
            ? (ytStatus?.channelTitle ?? "YouTube channel")
            : platform === "tiktok"
              ? tiktokAccount?.username || tiktokStatus?.tiktokUsername
                ? `@${tiktokAccount?.username ?? tiktokStatus?.tiktokUsername}`
                : "TikTok account"
              : platform === "linkedin"
                ? (linkedinAccount?.name ?? "LinkedIn account")
                : "Facebook page";

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }
    setSortField(field);
    setSortDir("desc");
  };

  const renderContentTable = (
    title: string,
    subtitle: string,
    showPlatform = platform === "summary"
  ) => (
    <Surface title={title} subtitle={subtitle} className="overflow-hidden">
      <div className="-mx-5 -mb-5 overflow-x-auto">
        <div className={showPlatform ? "min-w-[720px]" : "min-w-[640px]"}>
          <div
            className={
              showPlatform
                ? "grid grid-cols-[44px_1fr_76px_76px_60px_80px_80px_80px_80px] items-center gap-2 border-y border-white/[0.06] bg-white/[0.02] px-4 py-2"
                : "grid grid-cols-[44px_1fr_76px_60px_80px_80px_80px_80px] items-center gap-2 border-y border-white/[0.06] bg-white/[0.02] px-4 py-2"
            }
          >
            <div className="w-11" />
            <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-white/30">
              {t("table.content")}
            </span>
            <span className="text-right text-[10px] font-medium uppercase tracking-[0.16em] text-white/30">
              Typ
            </span>
            {showPlatform ? (
              <span className="text-right text-[10px] font-medium uppercase tracking-[0.16em] text-white/30">
                Network
              </span>
            ) : null}
            <SortableHeader
              label={t("table.date")}
              field="date"
              currentSort={sortField}
              currentDir={sortDir}
              onSort={handleSort}
            />
            <SortableHeader
              label={t("table.impressions")}
              field="impressions"
              currentSort={sortField}
              currentDir={sortDir}
              onSort={handleSort}
            />
            <SortableHeader
              label={t("table.comments")}
              field="comments"
              currentSort={sortField}
              currentDir={sortDir}
              onSort={handleSort}
            />
            <SortableHeader
              label={t("table.likes")}
              field="likes"
              currentSort={sortField}
              currentDir={sortDir}
              onSort={handleSort}
            />
            <SortableHeader
              label={t("table.shares")}
              field="shares"
              currentSort={sortField}
              currentDir={sortDir}
              onSort={handleSort}
            />
          </div>
          <div className="max-h-[640px] overflow-y-auto">
            {sortedInsights.length > 0 ? (
              sortedInsights.map((insight) => (
                <PostRow
                  key={insight.id}
                  insight={insight}
                  showPlatform={showPlatform}
                  onCrosspost={handleCrosspost}
                  crossposting={crosspostingId === insight.id}
                />
              ))
            ) : (
              <div className="px-4 py-12 text-center text-sm text-white/35">{t("noData")}</div>
            )}
          </div>
        </div>
      </div>
    </Surface>
  );

  return (
    <div className="flex-1 overflow-y-auto bg-background p-4">
      <div className="mx-auto max-w-[1500px]">
        <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <Card className="border-white/[0.08] bg-card shadow-none">
              <CardContent className="p-3">
                <div className="mb-3 px-2 pt-1">
                  <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/30">
                    Analytics
                  </div>
                </div>

                <div className="space-y-1">
                  <button
                    onClick={() => setPlatform("summary")}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-all ${
                      platform === "summary"
                        ? `border-white/10 bg-gradient-to-r ${SUMMARY_META.toneSoft} ${SUMMARY_META.cardGlow}`
                        : "border-transparent bg-white/[0.03] hover:border-white/[0.06] hover:bg-white/[0.05]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04]">
                        <BarChart3 size={16} style={{ color: SUMMARY_META.tone }} />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">Summary</div>
                        <div className="text-[11px] text-white/40">All connected platforms</div>
                      </div>
                    </div>
                    <div className="rounded-full border border-white/[0.08] bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-white/60">
                      {connectedPlatforms.length}
                    </div>
                  </button>

                  {connectedPlatforms.map((pid) => {
                    const meta = PLATFORM_META[pid];
                    const Icon = meta.icon;
                    const active = platform === pid;

                    const subtitle =
                      pid === "instagram"
                        ? `${igAccounts.length} account${igAccounts.length === 1 ? "" : "s"}`
                        : pid === "facebook"
                          ? `${fbPages.length} page${fbPages.length === 1 ? "" : "s"}`
                          : pid === "youtube"
                            ? (ytStatus?.channelTitle ?? "Connected")
                            : pid === "tiktok"
                              ? tiktokAccount?.username || tiktokStatus?.tiktokUsername
                                ? `@${tiktokAccount?.username ?? tiktokStatus?.tiktokUsername}`
                                : "Connected"
                              : (linkedinAccount?.name ?? "Connected");

                    return (
                      <button
                        key={pid}
                        onClick={() => setPlatform(pid)}
                        className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-all ${
                          active
                            ? `border-white/10 bg-gradient-to-r ${meta.toneSoft} ${meta.cardGlow}`
                            : "border-transparent bg-white/[0.03] hover:border-white/[0.06] hover:bg-white/[0.05]"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10"
                            style={{
                              backgroundColor: active
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(255,255,255,0.04)",
                            }}
                          >
                            <Icon size={16} style={{ color: meta.tone }} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">{meta.label}</div>
                            <div className="text-[11px] text-white/40">{subtitle}</div>
                          </div>
                        </div>
                        <div
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: meta.tone }}
                        />
                      </button>
                    );
                  })}
                </div>

                {connectedPlatforms.length === 0 && !accountsLoading && (
                  <div className="mt-3 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-3">
                    <div className="text-[11px] text-white/40 leading-5">
                      Nie masz jeszcze połączonej żadnej platformy. Podłącz konto, żeby zobaczyć
                      dane.
                    </div>
                    <button
                      onClick={goToIntegrations}
                      className="mt-2.5 w-full rounded-md bg-[#ef3e29] px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-[#d63520]"
                    >
                      Połącz platformę →
                    </button>
                  </div>
                )}

                <div className="mt-4 rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-3 py-3">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-white/30">
                    Coverage
                  </div>
                  <div className="mt-2 text-sm text-white">Organic content performance</div>
                  <div className="mt-1 text-xs leading-5 text-white/42">{t("coverageText")}</div>
                </div>
              </CardContent>
            </Card>

            {/* VP-99: client is chosen by the global selector near the logo — no inline Workspace
                picker here. IG/FB selection resets on client change via the effect above. */}
          </aside>

          <main className="space-y-4">
            {metaInfo && metaInfo.state !== "connected"
              ? (() => {
                  const selected = agencyClients.find(
                    (c: AgencyClientOption) => c.userId === selectedClientUserId
                  );
                  return (
                    <MetaConnectionBanner
                      info={metaInfo}
                      clientName={selected?.name}
                      clientId={selected?.id}
                      canManageIntegrations={canManageIntegrations}
                      onGoToIntegrations={goToIntegrations}
                      onGoToClient={(cid) =>
                        setLocation(cid ? `/agency-admin?clientId=${cid}` : "/agency-admin")
                      }
                    />
                  );
                })()
              : null}
            <Card className="border-white/[0.08] bg-card shadow-none">
              <CardContent className="p-4">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br ${activePlatformMeta.toneSoft}`}
                      >
                        <ActivePlatformIcon size={18} style={{ color: activePlatformMeta.tone }} />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-white/32">
                          Performance dashboard
                        </div>
                        <div className="text-xl font-semibold text-white">
                          {activePlatformMeta.label} analytics
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs text-white/44">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5">
                        <CalendarRange size={13} />
                        {currentPeriodLabel}
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5">
                        <ActivePlatformIcon size={13} />
                        {activeAccountLabel}
                      </span>
                      <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5">
                        {platform === "summary"
                          ? "Cross-platform organic rollup"
                          : "Window based on fetched organic content"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {platform === "summary" ? (
                      <>
                        {connectedPlatforms.includes("instagram") && (
                          <Select value={activeIgAccount} onValueChange={setSelectedIgAccount}>
                            <SelectTrigger className="h-10 w-[200px] border-white/[0.08] bg-white/[0.04] text-xs text-white">
                              <Instagram size={14} className="mr-2 text-white/40" />
                              <SelectValue placeholder={t("selectIgAccount")} />
                            </SelectTrigger>
                            <SelectContent>
                              {igAccounts.map((account) => (
                                <SelectItem key={account.igAccountId} value={account.igAccountId}>
                                  @{account.username}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {connectedPlatforms.includes("facebook") && (
                          <Select value={activeFbPage} onValueChange={setSelectedFbPage}>
                            <SelectTrigger className="h-10 w-[200px] border-white/[0.08] bg-white/[0.04] text-xs text-white">
                              <Facebook size={14} className="mr-2 text-white/40" />
                              <SelectValue placeholder={t("selectFbPage")} />
                            </SelectTrigger>
                            <SelectContent>
                              {fbPages.map((page) => (
                                <SelectItem key={page.pageId} value={page.pageId}>
                                  {page.pageName || page.pageId}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {connectedPlatforms.includes("youtube") && (
                          <div className="flex h-10 items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-xs text-white/60">
                            <Youtube size={14} style={{ color: PLATFORM_META.youtube.tone }} />
                            {ytStatus?.channelTitle ?? "YouTube"}
                          </div>
                        )}
                        {connectedPlatforms.includes("tiktok") && (
                          <div className="flex h-10 items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-xs text-white/60">
                            <Music2 size={14} style={{ color: PLATFORM_META.tiktok.tone }} />
                            {tiktokAccount?.username || tiktokStatus?.tiktokUsername
                              ? `@${tiktokAccount?.username ?? tiktokStatus?.tiktokUsername}`
                              : "TikTok"}
                          </div>
                        )}
                        {connectedPlatforms.includes("linkedin") && (
                          <div className="flex h-10 items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-xs text-white/60">
                            <Linkedin size={14} style={{ color: PLATFORM_META.linkedin.tone }} />
                            {linkedinAccount?.name ?? "LinkedIn"}
                          </div>
                        )}
                      </>
                    ) : platform === "instagram" ? (
                      <Select
                        value={activeIgAccount}
                        onValueChange={setSelectedIgAccount}
                        disabled={igAccounts.length === 0}
                      >
                        <SelectTrigger className="h-10 w-[240px] border-white/[0.08] bg-white/[0.04] text-xs text-white">
                          <Instagram size={14} className="mr-2 text-white/40" />
                          <SelectValue
                            placeholder={
                              igAccounts.length === 0 ? t("noIgAccounts") : t("selectIgAccount")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {igAccounts.map((account) => (
                            <SelectItem key={account.igAccountId} value={account.igAccountId}>
                              @{account.username}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : platform === "youtube" ? (
                      <div className="flex h-10 items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-xs text-white/60">
                        <Youtube size={14} style={{ color: PLATFORM_META.youtube.tone }} />
                        {ytStatus?.channelTitle ?? "YouTube channel"}
                      </div>
                    ) : platform === "tiktok" ? (
                      <div className="flex h-10 items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-xs text-white/60">
                        <Music2 size={14} style={{ color: PLATFORM_META.tiktok.tone }} />
                        {tiktokAccount?.username || tiktokStatus?.tiktokUsername
                          ? `@${tiktokAccount?.username ?? tiktokStatus?.tiktokUsername}`
                          : "TikTok account"}
                      </div>
                    ) : platform === "linkedin" ? (
                      <div className="flex h-10 items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 text-xs text-white/60">
                        <Linkedin size={14} style={{ color: PLATFORM_META.linkedin.tone }} />
                        {linkedinAccount?.name ?? "LinkedIn account"}
                      </div>
                    ) : (
                      <Select
                        value={activeFbPage}
                        onValueChange={setSelectedFbPage}
                        disabled={fbPages.length === 0}
                      >
                        <SelectTrigger className="h-10 w-[240px] border-white/[0.08] bg-white/[0.04] text-xs text-white">
                          <Facebook size={14} className="mr-2 text-white/40" />
                          <SelectValue
                            placeholder={fbPages.length === 0 ? t("noFbPages") : t("selectFbPage")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {fbPages.map((page) => (
                            <SelectItem key={page.pageId} value={page.pageId}>
                              {page.pageName || page.pageId}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {isLoading ? <LoadingState /> : null}

            {!isLoading && !hasAccounts && platform === "summary" ? (
              <div className="rounded-xl border border-dashed border-white/[0.08] bg-white/[0.02] px-8 py-14 text-center">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.04]">
                  <BarChart3 size={26} className="text-white/30" />
                </div>
                <div className="text-lg font-semibold text-white">
                  Połącz platformę, żeby zacząć
                </div>
                <p className="mt-2 mx-auto max-w-sm text-sm leading-6 text-white/40">
                  Analityka organiczna pojawi się tu od razu po połączeniu pierwszego konta. Możesz
                  podłączyć kilka platform naraz.
                </p>

                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  {[
                    {
                      id: "instagram",
                      icon: Instagram,
                      label: "Instagram",
                      tone: PLATFORM_META.instagram.tone,
                    },
                    {
                      id: "facebook",
                      icon: Facebook,
                      label: "Facebook",
                      tone: PLATFORM_META.facebook.tone,
                    },
                    // LinkedIn widoczny tylko dla super_admin (brak zatwierdzonego OAuth dla reszty)
                    ...(isSuperAdmin
                      ? [
                          {
                            id: "linkedin",
                            icon: Linkedin,
                            label: "LinkedIn",
                            tone: PLATFORM_META.linkedin.tone,
                          },
                        ]
                      : []),
                    {
                      id: "tiktok",
                      icon: Music2,
                      label: "TikTok",
                      tone: PLATFORM_META.tiktok.tone,
                    },
                  ].map(({ id, icon: Icon, label, tone }) => (
                    <div
                      key={id}
                      className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-2.5 text-sm text-white/50"
                    >
                      <Icon size={15} style={{ color: tone }} />
                      {label}
                    </div>
                  ))}
                </div>

                <button
                  onClick={goToIntegrations}
                  className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#ef3e29] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#d63520]"
                >
                  Przejdź do integracji
                  <Share2 size={14} />
                </button>
              </div>
            ) : null}

            {!isLoading && !hasAccounts && platform === "instagram" ? (
              <EmptyState
                message={t("emptyState.instagram")}
                actionLabel={t("emptyState.connectInstagram")}
                onAction={goToIntegrations}
              />
            ) : null}

            {!isLoading && !hasAccounts && platform === "facebook" ? (
              <EmptyState
                message={t("emptyState.facebook")}
                actionLabel={t("emptyState.connectFacebook")}
                onAction={goToIntegrations}
              />
            ) : null}

            {!isLoading && !hasAccounts && platform === "youtube" ? (
              <EmptyState
                message="Połącz YouTube, żeby zobaczyć analitykę filmów i Shorts."
                actionLabel="Przejdź do integracji"
                onAction={goToIntegrations}
              />
            ) : null}

            {!isLoading && !hasAccounts && platform === "tiktok" ? (
              <EmptyState
                message="Połącz TikTok żeby zobaczyć analityki swoich filmów — widoki, polubienia i zaangażowanie."
                actionLabel="Połącz TikTok →"
                onAction={goToIntegrations}
              />
            ) : null}

            {!isLoading && platform === "linkedin" ? (
              <EmptyState
                message="LinkedIn jest połączony. Szczegółowa analityka postów LinkedIn jest w przygotowaniu — pojawi się w kolejnej aktualizacji."
                actionLabel="Zarządzaj integracją →"
                onAction={goToIntegrations}
              />
            ) : null}

            {!isLoading && hasAccounts && platform !== "linkedin" ? (
              <Tabs
                value={tab}
                onValueChange={(value) => setTab(value as DashboardTab)}
                className="space-y-4"
              >
                <div className="overflow-x-auto">
                  <TabsList className="h-auto min-w-max rounded-lg border border-white/[0.08] bg-card p-1">
                    {TAB_LABELS.map((item) => {
                      const disabled =
                        (platform === "facebook" && item.id === "stories") ||
                        (platform === "youtube" && item.id === "stories") ||
                        (platform === "tiktok" && (item.id === "stories" || item.id === "reels")) ||
                        (platform === "summary" &&
                          item.id === "stories" &&
                          igAccounts.length === 0);
                      const label =
                        platform === "youtube"
                          ? item.id === "posts"
                            ? "Videos"
                            : item.id === "reels"
                              ? "Shorts"
                              : item.label
                          : item.label;
                      return (
                        <TabsTrigger
                          key={item.id}
                          value={item.id}
                          disabled={disabled}
                          className="rounded-md px-4 py-2 text-xs font-medium text-white/42 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white disabled:cursor-not-allowed disabled:opacity-35"
                        >
                          {label}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>

                <TabsContent value="summary" className="mt-0 space-y-4">
                  {platform === "summary" ? (
                    <>
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                        <StatCard
                          label="Platforms"
                          value={String(connectedPlatforms.length)}
                          hint="connected in this workspace"
                          icon={<BarChart3 size={16} className="text-white" />}
                          accentClass="bg-white/[0.08] text-white"
                        />
                        <StatCard
                          label="Content"
                          value={String(scopedKpis.postCount)}
                          hint={`${formatNumber(scopedKpis.postsPerWeek)} posts / week`}
                          icon={<FileText size={16} className="text-white" />}
                          accentClass="bg-white/[0.08] text-white"
                        />
                        <StatCard
                          label="Views"
                          value={formatNumber(scopedKpis.totalImpressions)}
                          hint="all platforms combined"
                          icon={<Eye size={16} className="text-orange-200" />}
                          accentClass="bg-orange-500/18"
                        />
                        <StatCard
                          label="Reach"
                          value={formatNumber(scopedKpis.totalReach)}
                          hint="all platforms combined"
                          icon={<Users size={16} className="text-emerald-200" />}
                          accentClass="bg-emerald-500/18"
                        />
                        <StatCard
                          label="Interactions"
                          value={formatNumber(scopedKpis.totalInteractions)}
                          hint="all platforms combined"
                          icon={<TrendingUp size={16} className="text-fuchsia-200" />}
                          accentClass="bg-fuchsia-500/18"
                        />
                      </div>

                      <Surface
                        title="Cross-platform interactions"
                        subtitle="Daily interaction trend split by connected platform"
                      >
                        <div className="mb-4 grid gap-3 xl:grid-cols-[260px_repeat(2,minmax(0,1fr))]">
                          <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                            <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                              Total interactions
                            </div>
                            <div className="mt-2 text-3xl font-semibold text-white">
                              {formatNumber(scopedKpis.totalInteractions)}
                            </div>
                            <div className="mt-1 text-xs text-white/42">
                              {formatPercent(scopedKpis.engagementRate)} engagement
                            </div>
                          </div>
                          {platformSnapshots.map(({ platform: itemPlatform, kpis }) => (
                            <div
                              key={itemPlatform}
                              className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4"
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-white">
                                  {getPlatformLabel(itemPlatform)}
                                </div>
                                <div
                                  className="h-2.5 w-2.5 rounded-full"
                                  style={{ backgroundColor: PLATFORM_META[itemPlatform].tone }}
                                />
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(kpis.totalInteractions)}
                              </div>
                              <div className="mt-1 text-xs text-white/42">
                                {formatNumber(kpis.postCount)} content
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={summaryInteractionsSeries}>
                              <CartesianGrid stroke={chartGrid} vertical={false} />
                              <XAxis
                                dataKey="label"
                                tick={{ fill: chartAxis, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                tick={{ fill: chartAxis, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                width={42}
                              />
                              <Tooltip content={<AnalyticsChartTooltip />} />
                              <Line
                                type="monotone"
                                dataKey="instagram"
                                name="Instagram"
                                stroke={PLATFORM_META.instagram.tone}
                                strokeWidth={2.2}
                                dot={{ r: 2.5, fill: PLATFORM_META.instagram.tone }}
                              />
                              <Line
                                type="monotone"
                                dataKey="facebook"
                                name="Facebook"
                                stroke={PLATFORM_META.facebook.tone}
                                strokeWidth={2.2}
                                dot={{ r: 2.5, fill: PLATFORM_META.facebook.tone }}
                              />
                              {ytConnected && (
                                <Line
                                  type="monotone"
                                  dataKey="youtube"
                                  name="YouTube"
                                  stroke={PLATFORM_META.youtube.tone}
                                  strokeWidth={2.2}
                                  dot={{ r: 2.5, fill: PLATFORM_META.youtube.tone }}
                                />
                              )}
                              {tiktokConnected && (
                                <Line
                                  type="monotone"
                                  dataKey="tiktok"
                                  name="TikTok"
                                  stroke={PLATFORM_META.tiktok.tone}
                                  strokeWidth={2.2}
                                  dot={{ r: 2.5, fill: PLATFORM_META.tiktok.tone }}
                                />
                              )}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </Surface>

                      <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
                        <Surface
                          title="Cross-platform publishing"
                          subtitle="Volume of content shipped per day by platform"
                        >
                          <div className="mb-4 grid gap-3 xl:grid-cols-[260px_repeat(2,minmax(0,1fr))]">
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Total content
                              </div>
                              <div className="mt-2 text-3xl font-semibold text-white">
                                {formatNumber(scopedKpis.postCount)}
                              </div>
                              <div className="mt-1 text-xs text-white/42">
                                combined content window
                              </div>
                            </div>
                            {platformSnapshots.map(({ platform: itemPlatform, kpis }) => (
                              <div
                                key={itemPlatform}
                                className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="text-sm font-medium text-white">
                                    {getPlatformLabel(itemPlatform)}
                                  </div>
                                  <div
                                    className="h-2.5 w-2.5 rounded-full"
                                    style={{ backgroundColor: PLATFORM_META[itemPlatform].tone }}
                                  />
                                </div>
                                <div className="mt-2 text-2xl font-semibold text-white">
                                  {formatNumber(kpis.postCount)}
                                </div>
                                <div className="mt-1 text-xs text-white/42">
                                  {formatNumber(kpis.postsPerWeek)} / week
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={summaryContentSeries}>
                                <CartesianGrid stroke={chartGrid} vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={38}
                                  allowDecimals={false}
                                />
                                <Tooltip content={<AnalyticsChartTooltip />} />
                                <Bar
                                  dataKey="instagram"
                                  name="Instagram"
                                  radius={[6, 6, 0, 0]}
                                  fill={PLATFORM_META.instagram.tone}
                                />
                                <Bar
                                  dataKey="facebook"
                                  name="Facebook"
                                  radius={[6, 6, 0, 0]}
                                  fill={PLATFORM_META.facebook.tone}
                                />
                                {ytConnected && (
                                  <Bar
                                    dataKey="youtube"
                                    name="YouTube"
                                    radius={[6, 6, 0, 0]}
                                    fill={PLATFORM_META.youtube.tone}
                                  />
                                )}
                                {tiktokConnected && (
                                  <Bar
                                    dataKey="tiktok"
                                    name="TikTok"
                                    radius={[6, 6, 0, 0]}
                                    fill={PLATFORM_META.tiktok.tone}
                                  />
                                )}
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </Surface>

                        <Surface
                          title="Platform mix"
                          subtitle="How current results split across connected networks"
                        >
                          <div className="space-y-4">
                            {platformSnapshots.map(({ platform: itemPlatform, kpis }) => {
                              const share =
                                scopedKpis.totalInteractions > 0
                                  ? (kpis.totalInteractions / scopedKpis.totalInteractions) * 100
                                  : 0;
                              return (
                                <div key={itemPlatform} className="space-y-2">
                                  <div className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-2 font-medium text-white">
                                      <span
                                        className="h-2.5 w-2.5 rounded-full"
                                        style={{
                                          backgroundColor: PLATFORM_META[itemPlatform].tone,
                                        }}
                                      />
                                      {getPlatformLabel(itemPlatform)}
                                    </div>
                                    <div className="text-white/50">
                                      {formatNumber(kpis.totalInteractions)} interactions
                                      <span className="text-white/28">
                                        {" "}
                                        ({formatPercent(share)})
                                      </span>
                                    </div>
                                  </div>
                                  <div className="h-2 rounded-full bg-white/[0.05]">
                                    <div
                                      className="h-2 rounded-full"
                                      style={{
                                        width: `${Math.min(100, share)}%`,
                                        backgroundColor: PLATFORM_META[itemPlatform].tone,
                                      }}
                                    />
                                  </div>
                                  <div className="flex items-center justify-between text-[11px] text-white/40">
                                    <span>{formatNumber(kpis.totalImpressions)} views</span>
                                    <span>{formatNumber(kpis.postCount)} content</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Surface>
                      </div>

                      {renderContentTable(
                        "Cross-platform content",
                        "Wszystkie treści z podziałem na network i wspólnym sortowaniem",
                        true
                      )}
                    </>
                  ) : (
                    <>
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                        <StatCard
                          label="Content"
                          value={String(scopedKpis.postCount)}
                          hint={`${formatNumber(scopedKpis.postsPerWeek)} posts / week`}
                          icon={<FileText size={16} className="text-white" />}
                          accentClass="bg-white/[0.08] text-white"
                        />
                        <StatCard
                          label="Views"
                          value={formatNumber(scopedKpis.totalImpressions)}
                          hint={`${formatNumber(scopedKpis.avgImpressionsPerContent)} avg / content`}
                          icon={<Eye size={16} className="text-orange-200" />}
                          accentClass="bg-orange-500/18"
                        />
                        <StatCard
                          label="Reach"
                          value={formatNumber(scopedKpis.totalReach)}
                          hint={`${formatNumber(scopedKpis.avgReachPerContent)} avg / content`}
                          icon={<Users size={16} className="text-emerald-200" />}
                          accentClass="bg-emerald-500/18"
                        />
                        <StatCard
                          label="Interactions"
                          value={formatNumber(scopedKpis.totalInteractions)}
                          hint={`${formatNumber(scopedKpis.avgInteractionsPerContent)} avg / content`}
                          icon={<TrendingUp size={16} className="text-fuchsia-200" />}
                          accentClass="bg-fuchsia-500/18"
                        />
                        <StatCard
                          label="Engagement"
                          value={formatPercent(scopedKpis.engagementRate)}
                          hint="interactions vs reach"
                          icon={<BarChart3 size={16} className="text-sky-200" />}
                          accentClass="bg-sky-500/18"
                        />
                      </div>

                      <Surface
                        title="Performance trend"
                        subtitle="Daily rollup of organic results with publishing cadence"
                        headerRight={
                          <div className="flex flex-wrap gap-2 text-[11px] text-white/48">
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-2.5 py-1">
                              <span className="h-2 w-2 rounded-full bg-sky-400" />
                              Reach
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-2.5 py-1">
                              <span className="h-2 w-2 rounded-full bg-fuchsia-400" />
                              Interactions
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-2.5 py-1">
                              <span className="h-2 w-2 rounded-full bg-amber-300" />
                              Content
                            </span>
                          </div>
                        }
                      >
                        <div className="h-[320px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={overviewSeries}>
                              <defs>
                                <linearGradient id="analytics-reach" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.35} />
                                  <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid stroke={chartGrid} vertical={false} />
                              <XAxis
                                dataKey="label"
                                tick={{ fill: chartAxis, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                yAxisId="left"
                                tick={{ fill: chartAxis, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                width={40}
                              />
                              <YAxis yAxisId="right" hide />
                              <Tooltip content={<AnalyticsChartTooltip />} />
                              <Bar
                                yAxisId="right"
                                dataKey="contentCount"
                                name="Content"
                                barSize={10}
                                radius={[6, 6, 0, 0]}
                                fill="rgba(251,191,36,0.6)"
                              />
                              <Area
                                yAxisId="left"
                                type="monotone"
                                dataKey="reach"
                                name="Reach"
                                stroke="#60a5fa"
                                fill="url(#analytics-reach)"
                                strokeWidth={2.2}
                              />
                              <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="interactions"
                                name="Interactions"
                                stroke="#f472b6"
                                strokeWidth={2.2}
                                dot={{ r: 2.5, fill: "#f472b6" }}
                                activeDot={{ r: 4 }}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </Surface>

                      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr_0.95fr]">
                        <Surface
                          title="Audience actions"
                          subtitle="Engagement breakdown from fetched content"
                        >
                          <div className="space-y-3">
                            {[
                              {
                                label: "Likes",
                                value: scopedKpis.totalLikes,
                                icon: <Heart size={14} className="text-red-300" />,
                              },
                              {
                                label: "Comments",
                                value: scopedKpis.totalComments,
                                icon: <MessageCircle size={14} className="text-sky-300" />,
                              },
                              {
                                label: "Shares",
                                value: scopedKpis.totalShares,
                                icon: <Share2 size={14} className="text-emerald-300" />,
                              },
                              {
                                label: "Saved",
                                value: scopedKpis.totalSaved,
                                icon: <Bookmark size={14} className="text-amber-300" />,
                              },
                              {
                                label: "Replies",
                                value: scopedKpis.totalReplies,
                                icon: <Reply size={14} className="text-violet-300" />,
                              },
                            ]
                              .filter((item) => item.value > 0)
                              .map((item) => {
                                const share =
                                  scopedKpis.totalInteractions > 0
                                    ? (item.value / scopedKpis.totalInteractions) * 100
                                    : 0;
                                return (
                                  <div key={item.label} className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs">
                                      <div className="flex items-center gap-2 text-white/62">
                                        {item.icon}
                                        {item.label}
                                      </div>
                                      <div className="text-white">
                                        {formatNumber(item.value)}{" "}
                                        <span className="text-white/35">
                                          ({formatPercent(share)})
                                        </span>
                                      </div>
                                    </div>
                                    <div className="h-2 rounded-full bg-white/[0.05]">
                                      <div
                                        className="h-2 rounded-full bg-white/70"
                                        style={{ width: `${Math.min(100, share)}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            {scopedKpis.totalInteractions === 0 ? (
                              <div className="rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-4 py-6 text-sm text-white/40">
                                Brak interakcji w aktualnym zakresie danych.
                              </div>
                            ) : null}
                          </div>
                        </Surface>

                        <Surface
                          title="Content mix"
                          subtitle="How each format contributes to performance"
                        >
                          <div className="space-y-4">
                            {contentMix.length > 0 ? (
                              contentMix.map((item) => {
                                const share =
                                  scopedKpis.postCount > 0
                                    ? (item.count / scopedKpis.postCount) * 100
                                    : 0;
                                return (
                                  <div key={item.bucket} className="space-y-2">
                                    <div className="flex items-center justify-between text-xs">
                                      <div className="font-medium text-white">
                                        {CONTENT_BUCKET_LABELS[item.bucket]}
                                      </div>
                                      <div className="text-white/48">
                                        {item.count} items{" "}
                                        <span className="text-white/28">
                                          ({formatPercent(share)})
                                        </span>
                                      </div>
                                    </div>
                                    <div className="h-2 rounded-full bg-white/[0.05]">
                                      <div
                                        className={`h-2 rounded-full ${
                                          item.bucket === "post"
                                            ? "bg-sky-400"
                                            : item.bucket === "reel"
                                              ? "bg-fuchsia-400"
                                              : "bg-orange-300"
                                        }`}
                                        style={{ width: `${Math.min(100, share)}%` }}
                                      />
                                    </div>
                                    <div className="flex items-center justify-between text-[11px] text-white/40">
                                      <span>{formatNumber(item.impressions)} views</span>
                                      <span>
                                        {formatNumber(item.avgInteractions)} avg interactions
                                      </span>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-4 py-6 text-sm text-white/40">
                                Brak wystarczających danych do zbudowania mixu treści.
                              </div>
                            )}
                          </div>
                        </Surface>

                        <Surface
                          title="Publishing rhythm"
                          subtitle="Which days are pulling the best average response"
                        >
                          <div className="space-y-3">
                            {weekdaySeries.map((item) => {
                              const maxValue = Math.max(
                                ...weekdaySeries.map((row) => row.avgInteractions),
                                1
                              );
                              const width = (item.avgInteractions / maxValue) * 100;
                              return (
                                <div
                                  key={item.label}
                                  className="grid grid-cols-[44px_1fr_auto] items-center gap-3"
                                >
                                  <div className="text-xs text-white/42">{item.label}</div>
                                  <div className="h-2.5 rounded-full bg-white/[0.05]">
                                    <div
                                      className="h-2.5 rounded-full bg-gradient-to-r from-white/55 to-white/80"
                                      style={{
                                        width: `${Math.max(width, item.contentCount > 0 ? 8 : 0)}%`,
                                      }}
                                    />
                                  </div>
                                  <div className="text-xs text-white">
                                    {item.contentCount > 0
                                      ? formatNumber(item.avgInteractions)
                                      : "0"}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {bestWeekday ? (
                            <div className="mt-4 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-3 text-xs text-white/55">
                              Best observed day:{" "}
                              <span className="font-medium text-white">{bestWeekday.label}</span>
                              <span className="text-white/35"> with </span>
                              <span className="font-medium text-white">
                                {formatNumber(bestWeekday.avgInteractions)}
                              </span>
                              <span className="text-white/35"> avg interactions per content.</span>
                            </div>
                          ) : null}
                        </Surface>
                      </div>

                      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
                        <Surface
                          title="Top content"
                          subtitle="Strongest items by total interactions"
                        >
                          <div className="space-y-3">
                            {topContent.slice(0, 5).map((item) => {
                              const thumbnail = item.thumbnailUrl ?? item.mediaUrl;
                              return (
                                <div
                                  key={item.id}
                                  className="flex items-center gap-3 rounded-lg border border-white/[0.05] bg-white/[0.02] px-3 py-3"
                                >
                                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-white/[0.05]">
                                    {thumbnail ? (
                                      <img
                                        src={thumbnail}
                                        alt=""
                                        className="h-full w-full object-cover"
                                      />
                                    ) : (
                                      <ImageOff size={14} className="text-white/20" />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="line-clamp-1 text-sm text-white/82">
                                      {item.caption || "Brak opisu treści"}
                                    </div>
                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-white/42">
                                      <span>{CONTENT_TYPE_LABELS[item.contentType]}</span>
                                      <span>•</span>
                                      <span>{formatDate(item.timestamp)}</span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm font-semibold text-white">
                                      {formatNumber(getInteractions(item.metrics))}
                                    </div>
                                    <div className="text-[11px] text-white/35">interactions</div>
                                  </div>
                                </div>
                              );
                            })}
                            {topContent.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-4 py-6 text-sm text-white/40">
                                Jeszcze nie mamy top content do pokazania dla tego widoku.
                              </div>
                            ) : null}
                          </div>
                        </Surface>

                        {renderContentTable(
                          "Content library",
                          "Wszystkie treści z aktualnego zakresu z możliwością sortowania"
                        )}
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="overview" className="mt-0 space-y-4">
                  {platform === "summary" ? (
                    <>
                      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
                        <Surface
                          title="Reach overview"
                          subtitle="Cross-platform reach trend for connected networks"
                        >
                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={summaryReachSeries}>
                                <defs>
                                  <linearGradient
                                    id="summary-total-reach"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                  >
                                    <stop offset="5%" stopColor="#c4b5fd" stopOpacity={0.34} />
                                    <stop offset="95%" stopColor="#c4b5fd" stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid stroke={chartGrid} vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={42}
                                />
                                <Tooltip content={<AnalyticsChartTooltip />} />
                                <Area
                                  type="monotone"
                                  dataKey="total"
                                  name="Total reach"
                                  stroke="#c4b5fd"
                                  fill="url(#summary-total-reach)"
                                  strokeWidth={2.2}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="instagram"
                                  name="Instagram"
                                  stroke={PLATFORM_META.instagram.tone}
                                  strokeWidth={1.8}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="facebook"
                                  name="Facebook"
                                  stroke={PLATFORM_META.facebook.tone}
                                  strokeWidth={1.8}
                                  dot={false}
                                />
                                {ytConnected && (
                                  <Line
                                    type="monotone"
                                    dataKey="youtube"
                                    name="YouTube"
                                    stroke={PLATFORM_META.youtube.tone}
                                    strokeWidth={1.8}
                                    dot={false}
                                  />
                                )}
                                {tiktokConnected && (
                                  <Line
                                    type="monotone"
                                    dataKey="tiktok"
                                    name="TikTok"
                                    stroke={PLATFORM_META.tiktok.tone}
                                    strokeWidth={1.8}
                                    dot={false}
                                  />
                                )}
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </Surface>

                        <Surface
                          title="Overview signals"
                          subtitle="Compact readout across all connected platforms"
                        >
                          <div className="grid gap-3">
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Average daily views
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(
                                  average(overviewSeries.map((item) => item.impressions))
                                )}
                              </div>
                            </div>
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Average daily reach
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(
                                  average(summaryReachSeries.map((item) => item.total))
                                )}
                              </div>
                            </div>
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Average daily interactions
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(
                                  average(summaryInteractionsSeries.map((item) => item.total))
                                )}
                              </div>
                            </div>
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Publishing frequency
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(allKpis.postsPerWeek)}
                              </div>
                              <div className="mt-1 text-xs text-white/42">
                                pieces per week across all connected platforms
                              </div>
                            </div>
                          </div>
                        </Surface>
                      </div>

                      <Surface
                        title="Platform account snapshot"
                        subtitle="How each connected platform contributes to overall reach and views"
                      >
                        <div className="grid gap-4 lg:grid-cols-2">
                          {platformSnapshots.map(({ platform: itemPlatform, kpis }) => (
                            <div
                              key={itemPlatform}
                              className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-4"
                            >
                              <div className="mb-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="h-2.5 w-2.5 rounded-full"
                                    style={{ backgroundColor: PLATFORM_META[itemPlatform].tone }}
                                  />
                                  <span className="text-sm font-medium text-white">
                                    {getPlatformLabel(itemPlatform)}
                                  </span>
                                </div>
                                <span className="text-[11px] text-white/38">
                                  {formatNumber(kpis.postCount)} content
                                </span>
                              </div>
                              <div className="grid gap-3 sm:grid-cols-3">
                                <div>
                                  <div className="text-[11px] uppercase tracking-[0.16em] text-white/28">
                                    Views
                                  </div>
                                  <div className="mt-1 text-lg font-semibold text-white">
                                    {formatNumber(kpis.totalImpressions)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[11px] uppercase tracking-[0.16em] text-white/28">
                                    Reach
                                  </div>
                                  <div className="mt-1 text-lg font-semibold text-white">
                                    {formatNumber(kpis.totalReach)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[11px] uppercase tracking-[0.16em] text-white/28">
                                    Interactions
                                  </div>
                                  <div className="mt-1 text-lg font-semibold text-white">
                                    {formatNumber(kpis.totalInteractions)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Surface>
                    </>
                  ) : (
                    <>
                      <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
                        <Surface
                          title="Daily overview"
                          subtitle="Views, reach and interactions across the current content window"
                        >
                          <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={overviewSeries}>
                                <defs>
                                  <linearGradient
                                    id="analytics-impressions"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                  >
                                    <stop
                                      offset="5%"
                                      stopColor={activePlatformMeta.tone}
                                      stopOpacity={0.38}
                                    />
                                    <stop
                                      offset="95%"
                                      stopColor={activePlatformMeta.tone}
                                      stopOpacity={0}
                                    />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid stroke={chartGrid} vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={42}
                                />
                                <Tooltip content={<AnalyticsChartTooltip />} />
                                <Area
                                  type="monotone"
                                  dataKey="impressions"
                                  name="Views"
                                  stroke={activePlatformMeta.tone}
                                  fill="url(#analytics-impressions)"
                                  strokeWidth={2.3}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="interactions"
                                  name="Interactions"
                                  stroke="#eab308"
                                  strokeWidth={2}
                                  dot={{ r: 2.5, fill: "#eab308" }}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </Surface>

                        <Surface
                          title="Overview signals"
                          subtitle="Compact readout for the current account"
                        >
                          <div className="grid gap-3">
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Average daily views
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(
                                  average(overviewSeries.map((item) => item.impressions))
                                )}
                              </div>
                            </div>
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Average daily interactions
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(
                                  average(overviewSeries.map((item) => item.interactions))
                                )}
                              </div>
                            </div>
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Publishing frequency
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {formatNumber(allKpis.postsPerWeek)}
                              </div>
                              <div className="mt-1 text-xs text-white/42">pieces per week</div>
                            </div>
                            <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-4">
                              <div className="text-[11px] uppercase tracking-[0.16em] text-white/32">
                                Best weekday
                              </div>
                              <div className="mt-2 text-2xl font-semibold text-white">
                                {bestWeekday?.label ?? "—"}
                              </div>
                              <div className="mt-1 text-xs text-white/42">
                                based on average interactions per content
                              </div>
                            </div>
                          </div>
                        </Surface>
                      </div>

                      <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
                        <Surface
                          title="Publishing cadence"
                          subtitle="How many pieces landed on each day"
                        >
                          <div className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={overviewSeries}>
                                <CartesianGrid stroke={chartGrid} vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  tick={{ fill: chartAxis, fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={38}
                                  allowDecimals={false}
                                />
                                <Tooltip content={<AnalyticsChartTooltip />} />
                                <Bar
                                  dataKey="contentCount"
                                  name="Content"
                                  radius={[8, 8, 0, 0]}
                                  fill={activePlatformMeta.tone}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </Surface>

                        <Surface
                          title="Format contribution"
                          subtitle="Volume and average response by content format"
                        >
                          <div className="space-y-4">
                            {contentMix.map((item) => (
                              <div
                                key={item.bucket}
                                className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-4"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="text-sm font-medium text-white">
                                    {CONTENT_BUCKET_LABELS[item.bucket]}
                                  </div>
                                  <div className="text-xs text-white/42">{item.count} items</div>
                                </div>
                                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                                  <div>
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-white/28">
                                      Views
                                    </div>
                                    <div className="mt-1 text-lg font-semibold text-white">
                                      {formatNumber(item.impressions)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-white/28">
                                      Reach
                                    </div>
                                    <div className="mt-1 text-lg font-semibold text-white">
                                      {formatNumber(item.reach)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-white/28">
                                      Avg interactions
                                    </div>
                                    <div className="mt-1 text-lg font-semibold text-white">
                                      {formatNumber(item.avgInteractions)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {contentMix.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-4 py-6 text-sm text-white/40">
                                Brak danych do przekroju formatów.
                              </div>
                            ) : null}
                          </div>
                        </Surface>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="posts" className="mt-0 space-y-4">
                  {scopedInsights.length > 0 ? (
                    <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
                      {renderContentTable(
                        "Posts performance",
                        "Każdy post z metrykami, sortowaniem i podglądem wyników"
                      )}
                      <div className="space-y-4">
                        <Surface
                          title="Posts snapshot"
                          subtitle="Quick readout for post content only"
                        >
                          <div className="grid grid-cols-2 gap-3">
                            <StatCard
                              label="Posts"
                              value={String(scopedKpis.postCount)}
                              hint="in current window"
                              icon={<FileText size={14} className="text-white" />}
                              accentClass="bg-white/[0.08]"
                            />
                            <StatCard
                              label="Views"
                              value={formatNumber(scopedKpis.totalImpressions)}
                              hint="total post views"
                              icon={<Eye size={14} className="text-orange-200" />}
                              accentClass="bg-orange-500/18"
                            />
                            <StatCard
                              label="Reach"
                              value={formatNumber(scopedKpis.totalReach)}
                              hint="total post reach"
                              icon={<Users size={14} className="text-emerald-200" />}
                              accentClass="bg-emerald-500/18"
                            />
                            <StatCard
                              label="Interactions"
                              value={formatNumber(scopedKpis.totalInteractions)}
                              hint="total post interactions"
                              icon={<TrendingUp size={14} className="text-fuchsia-200" />}
                              accentClass="bg-fuchsia-500/18"
                            />
                          </div>
                        </Surface>

                        <Surface
                          title="Best post"
                          subtitle="Highest interaction count in the current window"
                        >
                          {topContent[0] ? (
                            <div className="space-y-4">
                              <div className="flex h-48 items-center justify-center overflow-hidden rounded-lg bg-white/[0.04]">
                                {(topContent[0].thumbnailUrl ?? topContent[0].mediaUrl) ? (
                                  <img
                                    src={topContent[0].thumbnailUrl ?? topContent[0].mediaUrl}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <ImageOff size={18} className="text-white/20" />
                                )}
                              </div>
                              <div className="space-y-2">
                                <div className="line-clamp-3 text-sm leading-6 text-white/84">
                                  {topContent[0].caption || "Brak opisu treści"}
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs text-white/42">
                                  <span>{formatDate(topContent[0].timestamp)}</span>
                                  <span>•</span>
                                  <span>
                                    {formatNumber(getInteractions(topContent[0].metrics))}{" "}
                                    interactions
                                  </span>
                                  <span>•</span>
                                  <span>
                                    {formatNumber(getImpressions(topContent[0].metrics))} views
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <EmptyState message="Brak posta do wyróżnienia." />
                          )}
                        </Surface>
                      </div>
                    </div>
                  ) : (
                    <EmptyState message="Nie ma jeszcze danych dla postów w aktualnym zakresie." />
                  )}
                </TabsContent>

                <TabsContent value="reels" className="mt-0 space-y-4">
                  {scopedInsights.length > 0 ? (
                    <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
                      {renderContentTable(
                        "Reels performance",
                        "Reels z wyświetleniami, zasięgiem i interakcjami"
                      )}
                      <Surface
                        title="Reels trend"
                        subtitle="Daily reel performance within the current dataset"
                      >
                        <div className="h-[360px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={dailySeries}>
                              <defs>
                                <linearGradient id="analytics-reels" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#f472b6" stopOpacity={0.34} />
                                  <stop offset="95%" stopColor="#f472b6" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid stroke={chartGrid} vertical={false} />
                              <XAxis
                                dataKey="label"
                                tick={{ fill: chartAxis, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                tick={{ fill: chartAxis, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                width={42}
                              />
                              <Tooltip content={<AnalyticsChartTooltip />} />
                              <Area
                                type="monotone"
                                dataKey="impressions"
                                name="Views"
                                stroke="#f472b6"
                                fill="url(#analytics-reels)"
                                strokeWidth={2.2}
                              />
                              <Line
                                type="monotone"
                                dataKey="interactions"
                                name="Interactions"
                                stroke="#fb7185"
                                strokeWidth={2}
                                dot={{ r: 2.5, fill: "#fb7185" }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </Surface>
                    </div>
                  ) : (
                    <EmptyState message="Brak reelsów w aktualnym zestawie danych. Gdy pojawią się wyniki z Reels, ten widok od razu się wypełni." />
                  )}
                </TabsContent>

                <TabsContent value="stories" className="mt-0 space-y-4">
                  {scopedInsights.length > 0 ? (
                    <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
                      {renderContentTable(
                        "Stories performance",
                        "Stories z podstawowymi wskaźnikami oraz aktywnością"
                      )}
                      <Surface
                        title="Stories snapshot"
                        subtitle="Story-specific response from the current data window"
                      >
                        <div className="grid gap-3 sm:grid-cols-2">
                          <StatCard
                            label="Stories"
                            value={String(scopedKpis.postCount)}
                            hint="published stories"
                            icon={<Play size={14} className="text-white" />}
                            accentClass="bg-white/[0.08]"
                          />
                          <StatCard
                            label="Views"
                            value={formatNumber(scopedKpis.totalImpressions)}
                            hint="story impressions"
                            icon={<Eye size={14} className="text-orange-200" />}
                            accentClass="bg-orange-500/18"
                          />
                          <StatCard
                            label="Reach"
                            value={formatNumber(scopedKpis.totalReach)}
                            hint="story reach"
                            icon={<Users size={14} className="text-emerald-200" />}
                            accentClass="bg-emerald-500/18"
                          />
                          <StatCard
                            label="Replies"
                            value={formatNumber(scopedKpis.totalReplies)}
                            hint="direct responses"
                            icon={<Reply size={14} className="text-violet-200" />}
                            accentClass="bg-violet-500/18"
                          />
                        </div>
                      </Surface>
                    </div>
                  ) : (
                    <EmptyState message="Brak stories w aktualnym zakresie. Gdy Meta zwróci historyczne stories z insightami, ten panel pokaże ich wyniki." />
                  )}
                </TabsContent>
              </Tabs>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
